import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import gsap from 'gsap'
import { registerTransitionHandler } from '../../utils/transitions'

// ─────────────────────────────────────────────────────────────────────────────
// Default transition for nav links:
//   'iris'   → Black camera-aperture circle closes then reopens
//   'glitch' → Stroboscopic darkroom flash
//
// Gallery polaroid cards always use 'polaroid' (shared-element drop).
// ─────────────────────────────────────────────────────────────────────────────
const VARIANT = 'iris'

// Polaroid card metrics — must stay in sync with Polaroid in SceneGallery.jsx
const CARD_PAD_TOP    = 8
const CARD_PAD_SIDES  = 8
const CARD_PAD_BOTTOM = 52

export default function PageTransitionOverlay() {
  const navigate      = useNavigate()
  const irisRef       = useRef(null)
  const polaroidRef   = useRef(null)
  const polaroidImg   = useRef(null)
  const polaroidLabel = useRef(null)
  const busy          = useRef(false)

  useEffect(() => {
    registerTransitionHandler((path, opts = {}) => {
      if (busy.current) return
      busy.current = true

      const type = opts.type || VARIANT
      const el   = irisRef.current

      // ── IRIS ───────────────────────────────────────────────────────────────
      if (type === 'iris') {
        gsap.set(el, {
          clipPath:   'circle(0% at 50% 50%)',
          background: '#000',
          opacity:    1,
        })
        gsap.to(el, {
          clipPath: 'circle(150% at 50% 50%)',
          duration: 0.52,
          ease:     'power3.inOut',
          onComplete() {
            navigate(path)
            gsap.to(el, {
              clipPath: 'circle(0% at 50% 50%)',
              duration: 0.52,
              ease:     'power3.inOut',
              delay:    0.06,
              onComplete() { busy.current = false },
            })
          },
        })

      // ── GLITCH ─────────────────────────────────────────────────────────────
      } else if (type === 'glitch') {
        gsap.set(el, { clipPath: 'none', background: '#000', opacity: 0 })
        gsap.timeline()
          .to(el, { opacity: 0.55, duration: 0.04, ease: 'none' })
          .to(el, { opacity: 0.08, duration: 0.04, ease: 'none' })
          .to(el, { opacity: 0.80, duration: 0.04, ease: 'none' })
          .to(el, { opacity: 0.15, duration: 0.03, ease: 'none' })
          .to(el, { opacity: 1,    duration: 0.05, ease: 'none' })
          .call(() => {
            el.style.background = '#fff'
            navigate(path)
          })
          .to(el, { opacity: 0, duration: 0.30, ease: 'power2.out', delay: 0.04 })
          .call(() => {
            el.style.background = '#000'
            el.style.clipPath   = 'circle(0% at 50% 50%)'
            busy.current = false
          })

      // ── POLAROID DROP — Shared-element expand ──────────────────────────────
      //
      //  Phase 1 (0 → 180ms):  Card lifts straight up — shadow deepens,
      //                         rotation eases toward flat, slight scale boost.
      //  Phase 2 (180 → 760ms): Flies to full viewport while the polaroid
      //                         frame (padding) collapses to 0 and border-radius
      //                         shrinks away. Label burns in at the bottom.
      //  Phase 3 (760ms):       navigate() fires.
      //  Phase 4 (760 → 1100ms): Overlay fades out, revealing the new page.
      // ────────────────────────────────────────────────────────────────────────
      } else if (type === 'polaroid') {
        const { rect, rotate = 0, src } = opts
        const pEl  = polaroidRef.current
        const pImg = polaroidImg.current
        const pLbl = polaroidLabel.current

        if (src && pImg) pImg.src = src

        const vw = window.innerWidth
        const vh = window.innerHeight

        // ── Start: exact card position & style ──────────────────────────────
        gsap.set(pEl, {
          display:         'block',
          opacity:         1,
          left:            rect.left,
          top:             rect.top,
          width:           rect.width,
          height:          rect.height,
          rotation:        rotate,
          scale:           1,
          y:               0,
          paddingTop:      CARD_PAD_TOP,
          paddingRight:    CARD_PAD_SIDES,
          paddingBottom:   CARD_PAD_BOTTOM,
          paddingLeft:     CARD_PAD_SIDES,
          borderRadius:    3,
          boxShadow:       '0 14px 40px rgba(0,0,0,0.72), 0 2px 8px rgba(0,0,0,0.38)',
        })
        gsap.set(pLbl, { opacity: 0, y: 8 })

        const tl = gsap.timeline()

        // Phase 1 — lift ─────────────────────────────────────────────────────
        tl.to(pEl, {
          scale:     1.06,
          y:         -14,
          rotation:  rotate * 0.25,
          boxShadow: '0 32px 90px rgba(0,0,0,0.88), 0 10px 28px rgba(0,0,0,0.60)',
          duration:  0.18,
          ease:      'power2.out',
        })

        // Phase 2 — fly + frame collapse ─────────────────────────────────────
        .to(pEl, {
          left:          0,
          top:           0,
          width:         vw,
          height:        vh,
          rotation:      0,
          scale:         1,
          y:             0,
          paddingTop:    0,
          paddingRight:  0,
          paddingBottom: 0,
          paddingLeft:   0,
          borderRadius:  0,
          boxShadow:     'none',
          duration:      0.58,
          ease:          'power3.inOut',
        }, '<')

        // Label fades in mid-flight then out
        .to(pLbl, { opacity: 1, y: 0, duration: 0.22, ease: 'power2.out' }, '-=0.40')
        .to(pLbl, { opacity: 0,       duration: 0.18, ease: 'power2.in'  }, '-=0.10')

        // Phase 3 — navigate ──────────────────────────────────────────────────
        .call(() => navigate(path))

        // Phase 4 — fade out, reveal page ─────────────────────────────────────
        .to(pEl, {
          opacity:  0,
          duration: 0.32,
          ease:     'power2.out',
          delay:    0.06,
          onComplete() {
            gsap.set(pEl, {
              display: 'none', opacity: 1,
              paddingTop: 0, paddingRight: 0,
              paddingBottom: 0, paddingLeft: 0,
              scale: 1, y: 0,
            })
            busy.current = false
          },
        })
      }
    })

    return () => registerTransitionHandler(null)
  }, [navigate])

  return (
    <>
      {/* ── Iris + glitch overlay ────────────────────────────────────────── */}
      <div
        ref={irisRef}
        aria-hidden="true"
        style={{
          position:      'fixed',
          inset:         0,
          zIndex:        99990,
          pointerEvents: 'none',
          background:    '#000',
          clipPath:      'circle(0% at 50% 50%)',
        }}
      />

      {/* ── Polaroid expand overlay ─────────────────────────────────────── */}
      <div
        ref={polaroidRef}
        aria-hidden="true"
        style={{
          display:         'none',
          position:        'fixed',
          zIndex:          99991,
          pointerEvents:   'none',
          background:      'linear-gradient(150deg, #eeeae0 0%, #e6e2d6 100%)',
          overflow:        'hidden',
          willChange:      'left, top, width, height, transform',
        }}
      >
        {/* Photo fills the polaroid frame */}
        <img
          ref={polaroidImg}
          alt=""
          draggable={false}
          style={{
            width:      '100%',
            height:     '100%',
            objectFit:  'cover',
            display:    'block',
            filter:     'none',
          }}
        />

        {/* Caption burns in during the flight phase */}
        <div
          ref={polaroidLabel}
          style={{
            position:      'absolute',
            bottom:        0,
            left:          0,
            right:         0,
            padding:       '10px 14px',
            textAlign:     'center',
            fontFamily:    '"JetBrains Mono", monospace',
            fontSize:      9,
            letterSpacing: '0.30em',
            textTransform: 'uppercase',
            color:         'rgba(28,24,20,0.55)',
            background:    'linear-gradient(0deg, rgba(230,226,214,0.9) 0%, transparent 100%)',
            userSelect:    'none',
          }}
        >
          DREW HALLÉ · PHOTOGRAPHY
        </div>
      </div>
    </>
  )
}
